import * as React from 'react';
import Box from '@mui/material/Box';
import { Grid, TextField, Typography } from '@mui/material';
import { ReactComponent as LocationIcon } from '../../../assets/icons/Location.svg';
import CountrySelect from '../../shared/input/select/CountrySelect';
import { Country, Region } from '@esg/esg-global-types';
import { RegionExtended, createRegions, getRegions } from '../../../lib/metric_capture/region';
import { FeedbackSnackbarContext } from '../../../context/FeedbackSnackbarContext';
import { hasBannedCharacters } from '../../../lib/validation/text_validation';
import { MetadataError } from '@ep/error-handling';
import { generateUuidV4 } from '../../../util/uuid';
import { log } from '../../../util/log';
import { GroupContext } from '../../../context/GroupContext';
import { CompanyContext } from '../../../context/CompanyContext';
import RegionSelect from '../../shared/input/select/RegionSelect';
import GhostButton from '../../shared/button/GhostButton';
import PrimaryButton from '../../shared/button/PrimaryButton';
import PrimaryTabs from '../../shared/tab/PrimaryTabs';
import PrimaryTab from '../../shared/tab/PrimaryTab';

interface AddLocationWidgetProps {
  existing_regions: Array<Region>;
  handleLocationsToParent: (locations: Array<Region>) => void;
  closeWidget: () => void;
}

export default function AddLocationWidget(props: AddLocationWidgetProps) {
  const { setFeedbackData } = React.useContext(FeedbackSnackbarContext);
  const { group } = React.useContext(GroupContext);
  const { company } = React.useContext(CompanyContext);
  const [tab, setTab] = React.useState(0);
  const [country, setCountry] = React.useState<Country>();
  const [customRegionName, setCustomRegionName] = React.useState<string>('');
  const [regions, setRegions] = React.useState<Array<Region>>([]);
  const [addLoading, setAddLoading] = React.useState<boolean>(false);
  const [newLocations, setNewLocations] = React.useState<Array<RegionExtended>>([]);

  const invalidRegion = customRegionName
    ? hasBannedCharacters(customRegionName)
    : customRegionName.length < 1
      ? false
      : true;

  const input_validity = {
    duplicate_name: !props.existing_regions.some((region) => {
      return region.name.toLowerCase() === customRegionName.toLowerCase();
    })
      ? ''
      : `Region name already exists for ${company?.name}`
  };

  const countryFromParent = (country: Array<Country> | Country | null) => {
    if (!Array.isArray(country)) {
      setCountry(country ? country : undefined);
      setNewLocations([]);
    }
  };
  const handleTabChange = (event: React.SyntheticEvent, new_tab_value: number) => {
    setTab(new_tab_value);
    setCustomRegionName('');
    setNewLocations([]);
  };

  const handleNewRegionsFromSelector = (new_regions: Array<Region>) => {
    if (country && country.continent !== undefined) {
      setNewLocations(
        new_regions.map((region: Region) => {
          return {
            id: region.id,
            modified: region.modified,
            deleted: region.deleted,
            name: region.name,
            country: {
              id: country.code,
              modified: country.modified,
              deleted: null,
              name: country.name,
              continent: country.continent,
              code: country.code
            }
          };
        })
      );
    }
  };

  const createLocations = async () => {
    setAddLoading(true);
    try {
      if (group && company) {
        await createRegions(false, newLocations, group.id, company.id)
          .then(() => {
            props.handleLocationsToParent(newLocations);
          })
          .catch((error) => {
            throw new Error(error);
          });
      }
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error
            ? err.message
            : 'Error: AddLocationWidget failed on an unknown error while calling createLocation.',
          {
            newLocations: newLocations,
            group: group,
            company: company
          },
          tracking_id
        )
      );
      setFeedbackData({
        message: `Error creating location. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
    } finally {
      setAddLoading(false);
    }
  };

  React.useEffect(() => {
    (async () => {
      const master_list_regions: Array<Region> = await getRegions(true);
      setRegions(master_list_regions);
    })();
  }, []);

  return (
    <Box sx={{ p: 2 }}>
      <Grid container spacing={6}>
        {/* Widget Header */}
        <Grid size={10}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap'
            }}
          >
            <LocationIcon height={40} style={{ marginRight: 10 }} />
            <Typography variant="h5" color="inherit" align="left">
              Add Locations
            </Typography>
          </Box>
        </Grid>
        {props.existing_regions.length > 0 && (
          <>
            <Grid size={12}>
              <Typography>Configured Regions</Typography>
            </Grid>
            <Grid size={12} sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
              {props.existing_regions.map((region, index) => (
                <Typography
                  key={index}
                  sx={{
                    backgroundColor: '#E7E8E9',
                    color: '#454545',
                    borderRadius: 3,
                    px: 1.3,
                    py: 0.2,
                    whiteSpace: 'nowrap'
                  }}
                >
                  {region.name}
                </Typography>
              ))}
            </Grid>
          </>
        )}
        <Grid size={12}>
          <CountrySelect
            master_list
            selected_options={country}
            handleChangeCountries={countryFromParent}
          />
        </Grid>
        {country && (
          <Grid size={12}>
            <PrimaryTabs value={tab} onChange={handleTabChange} aria-label="region-input-tabs">
              <PrimaryTab label="Custom Region" />
              <PrimaryTab label="Standard Regions" />
            </PrimaryTabs>
          </Grid>
        )}
        {country && tab === 0 && (
          <Grid size={12}>
            <TextField
              fullWidth
              id="region-name-input"
              label="Region Name"
              variant="standard"
              error={input_validity.duplicate_name ? true : false || invalidRegion}
              helperText={input_validity.duplicate_name}
              value={customRegionName}
              onChange={(event: React.ChangeEvent<HTMLInputElement>): void => {
                setCustomRegionName(event.target.value);
                setNewLocations(
                  event.target.value
                    ? [
                        {
                          id: `${country.code}_${event.target.value[0]}${event.target.value
                            .substring(1)
                            .match(/[^aeiou\d_\W]/gi)
                            ?.join('')}`.toUpperCase(),
                          modified: new Date(),
                          country: { ...country },
                          deleted: null,
                          name: event.target.value
                        }
                      ]
                    : []
                );
              }}
            />
          </Grid>
        )}
        {country && tab === 1 && (
          <Grid size={12}>
            <RegionSelect
              tag_limit={3}
              multi_select
              variant="outlined"
              options={regions.filter((region: Region) => {
                return (
                  region.country.id === country?.id &&
                  !props.existing_regions.some(
                    (existing_region) => existing_region.id === region.id
                  )
                );
              })}
              selected_options={newLocations}
              handleChangeRegions={(regions: Region | Array<Region> | null) => {
                if (Array.isArray(regions)) {
                  handleNewRegionsFromSelector(regions);
                }
              }}
            />
          </Grid>
        )}
        {/* Button Input */}
        <Grid size={12} alignItems={'right'}>
          <Box m={1} display="flex" justifyContent="flex-end" alignItems="flex-end">
            <GhostButton
              outlined
              dark
              onClick={() => {
                props.closeWidget();
              }}
              sx={{ mr: 1 }}
            >
              Cancel
            </GhostButton>
            {
              <PrimaryButton
                loading={addLoading}
                disabled={
                  !(input_validity.duplicate_name === '') ||
                  invalidRegion ||
                  newLocations.length < 1
                }
                onClick={async () => {
                  await createLocations();
                  props.closeWidget();
                }}
              >
                Create
              </PrimaryButton>
            }
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}
